import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchUserProfile } from "../redux/slices/profileSlice";
import { setToken } from "../redux/slices/authSlice";
import { useDispatch } from "react-redux";

function SignUp() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    referralCode: "",
    profilePic: "",
  });

  /* true only when referralCode was injected from the URL — locks the field */
  const [referralFromUrl, setReferralFromUrl] = useState(false);

  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("signup"); // "signup" | "otp"
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  /* redirect if already logged in */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) navigate("/dashboard");
  }, []);

  /* prefill referral code from URL ?referral=XXX */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referral = params.get("referral");
    if (referral) {
      setForm((prev) => ({ ...prev, referralCode: referral }));
      setReferralFromUrl(true);
    }
  }, [location.search]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const validateSignup = () => {
    if (!form.name.trim()) return "Full Name is required.";
    if (!form.email.trim()) return "Email is required.";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim()))
      return "Enter a valid email.";
    if (!form.phone.trim()) return "Phone is required.";
    if (!/^\d{10}$/.test(form.phone.trim()))
      return "Enter a valid 10-digit phone number.";
    return "";
  };

  const validateOtp = () => {
    const clean = otp.trim();
    if (!clean) return "OTP is required.";
    if (!/^\d{4}$/.test(clean)) return "Enter a valid 4-digit OTP.";
    return "";
  };

  const readErrorMessage = async (res) => {
    try {
      const data = await res.json();
      return (
        data?.message ||
        data?.error ||
        data?.Message ||
        `Request failed (${res.status})`
      );
    } catch {
      const txt = await res.text().catch(() => "");
      return txt || `Request failed (${res.status})`;
    }
  };

  const signupPayload = () => ({
    phone: form.phone.trim(),
    name: form.name.trim(),
    email: form.email.trim(),
    profilePic: form.profilePic || "",
    referralCode: form.referralCode.trim() || "",
  });

  const handleSignup = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    const v = validateSignup();
    if (v) return setErrorMsg(v);

    setLoading(true);
    try {
      const res = await fetch(
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/Auth/Signup",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(signupPayload()),
        },
      );
      if (!res.ok) throw new Error(await readErrorMessage(res));
      setStep("otp");
      setOtp("");
    } catch (err) {
      setErrorMsg(err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    const v = validateOtp();
    if (v) return setErrorMsg(v);

    setLoading(true);
    try {
      const res = await fetch(
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/Auth/VerifyOtp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phone: form.phone.trim(), otp: otp.trim() }),
        },
      );
      if (!res.ok) throw new Error(await readErrorMessage(res));
      const data = await res.json().catch(() => ({}));
      if (data?.token) {
        dispatch(setToken(data.token));
        dispatch(fetchUserProfile());
        navigate("/dashboard");
      }
    } catch (err) {
      setErrorMsg(err?.message || "Invalid OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setErrorMsg("");
    setOtp("");
    setLoading(true);
    try {
      const res = await fetch(
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/Auth/Signup",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(signupPayload()),
        },
      );
      if (!res.ok) throw new Error(await readErrorMessage(res));
    } catch (err) {
      setErrorMsg(err?.message || "Failed to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  const isSignup = step === "signup";
  const isOtp = step === "otp";

  return (
    <>
      <main className="blue-light-bg">
        <section className="container-fluid">
          <div className="row min-vh-100">
            <div className="offset-xl-1 col-xl-5 col-lg-6 col-md-12 col-12 d-flex align-items-center">
              <div className="py-8 me-xl-8 pe-xl-8">
                <div>
                  <img
                    src="/assets/images/icon.png"
                    width="70"
                    className="me-5 d-lg-block d-none mb-2"
                  />
                  <h1 className="display-6 fw-bold m-0 lh-sm">
                    Create Account
                  </h1>
                  <p className="fs-4">
                    Already have an account?
                    <Link to="/login" className="ms-1">
                      Login
                    </Link>
                  </p>

                  <div className="mt-6 fs-4">
                    <p className="mb-4 lh-sm">
                      If you are seeking support please send us mail on{" "}
                      <a href="mailto:contact@khayatgroup.in">contact@khayatgroup.in</a> before reaching out
                      directly.
                    </p>
                    <p className="mb-1">
                      <i className="bi bi-telephone text-primary me-2" />
                      (+91) 9988776655
                    </p>
                    <p className="mb-1">
                      <i className="bi bi-envelope-open text-primary me-2" />
                      contact@khayatgroup.in
                    </p>
                  </div>

                  <div className="mt-8">
                    <div className="d-flex align-items-center">
                      <div class="d-flex gap-1 align-items-center justify-content-end me-lg-5 me-2">
                        <a href="https://www.facebook.com/" target="_blank">
                          <img src="/assets/images/facebook.png" width="30" />
                        </a>
                        <a href="https://www.instagram.com/" target="_blank">
                          <img src="/assets/images/instagram.png" width="30" />
                        </a>
                        <a href="https://x.com/" target="_blank">
                          <img src="/assets/images/x.png" width="30" />
                        </a>
                        <a href="https://www.youtube.com/" target="_blank">
                          <img src="/assets/images/youtube.png" width="30" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6 d-lg-flex align-items-center w-lg-50 position-fixed-lg bg-white top-0 right-0">
              <div className="px-4 px-xl-8 py-8 py-lg-0">
                <div>
                  <form onSubmit={isSignup ? handleSignup : handleVerifyOtp}>
                    <div>
                      <div className="row g-2">
                        {errorMsg ? (
                          <div className="col-12 mt-2">
                            <div className="alert alert-danger py-2 mb-2">
                              {errorMsg}
                            </div>
                          </div>
                        ) : null}

                        {isSignup ? (
                          <>
                            <div className="col-12 mb-1 mt-2">
                              <h1 className="h3 fw-bold lh-1 m-0">
                                Basic Details
                              </h1>
                              <p className="fs-4">
                                Welcome, Create your account in 2 minutes.
                              </p>
                            </div>

                            <div className="col-6 mb-2 mt-1">
                              <label className="form-label">
                                Full Name <b className="text-danger">*</b>
                              </label>
                              <input
                                name="name"
                                value={form.name}
                                onChange={onChange}
                                type="text"
                                className="form-control"
                                placeholder="Enter Full Name"
                                disabled={loading}
                              />
                            </div>

                            <div className="col-6 mb-2 mt-1">
                              <label className="form-label">
                                Email ID <b className="text-danger">*</b>
                              </label>
                              <input
                                name="email"
                                value={form.email}
                                onChange={onChange}
                                type="text"
                                className="form-control"
                                placeholder="Enter Email ID"
                                disabled={loading}
                              />
                            </div>

                            <div className="col-6 mb-4 mt-1">
                              <label className="form-label">
                                Phone No. <b className="text-danger">*</b>
                              </label>
                              <input
                                name="phone"
                                value={form.phone}
                                onChange={onChange}
                                type="text"
                                className="form-control"
                                placeholder="Enter Phone No."
                                disabled={loading}
                                inputMode="numeric"
                              />
                            </div>

                            <hr className="py-1" />

                            <div className="col-12 mb-2 mt-2">
                              <h1 className="h3 fw-bold lh-1 m-0">
                                Referral Link
                              </h1>
                              <p className="fs-4">
                                Enter if any Referral Code you have.
                              </p>

                              <div className="col-6">
                                <label className="form-label">
                                  Referral Code
                                </label>
                                <input
                                  name="referralCode"
                                  value={form.referralCode}
                                  onChange={onChange}
                                  type="text"
                                  className={`form-control ${referralFromUrl ? "bg-light" : ""}`}
                                  placeholder="Enter Referral Code"
                                  disabled={loading}
                                  // readOnly ONLY when pre-filled from URL, never when user is typing
                                  readOnly={referralFromUrl}
                                />
                                {referralFromUrl && (
                                  <div className="form-text text-success">
                                    <i className="bi bi-check-circle me-1" />
                                    Referral code applied
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        ) : null}

                        {isOtp ? (
                          <div className="col-12 mb-3 mt-2">
                            <h1 className="h3 fw-bold lh-1 m-0">Verify OTP</h1>
                            <p className="fs-4">
                              We have sent an OTP to your mobile (+91){" "}
                              {form.phone}
                            </p>

                            <div className="col-6">
                              <label className="form-label">
                                Enter 4 Digit OTP
                              </label>
                              <input
                                value={otp}
                                onChange={(e) =>
                                  setOtp(
                                    e.target.value
                                      .replace(/\D/g, "")
                                      .slice(0, 4),
                                  )
                                }
                                type="text"
                                className="form-control"
                                placeholder="Enter OTP"
                                disabled={loading}
                                inputMode="numeric"
                              />
                            </div>

                            <div className="col-12 mt-3">
                              <button
                                type="button"
                                className="btn text-danger p-0"
                                onClick={handleResendOtp}
                                disabled={loading}
                              >
                                <i className="bi bi-arrow-clockwise me-1" />{" "}
                                Resend OTP
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="d-grid col-7 mt-3">
                      <button
                        type="submit"
                        className="btn btn-primary rounded-3"
                        disabled={loading}
                      >
                        {loading ? (
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : isSignup ? (
                          "Create Account"
                        ) : (
                          "Verify OTP"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default SignUp;
